/**
 * Chat changes — the edits the agent made with ``doc_propose_changes``, as
 * the chat shows them: C-id links in the reply that jump Word to the spot and
 * open a diff, and Undo / Redo under the reply.
 *
 * The server sends one record per change on the turn's ``complete`` event
 * (stored on the ``askwhy`` log entry). Undo / Redo state lives here, keyed
 * by message + change id, so the reply's buttons and every link's popover
 * agree. Undo applies each change's inverse through the same quote-located
 * path the agent's edits used (Office.js can't drive Word's own Undo stack);
 * Redo applies the original change again.
 */

import { useSyncExternalStore } from "react";
import { type ChangeSpec, applyChanges } from "../../services/changeSet";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import { navigateToSection, selectSpanInSection } from "../../services/wordWriter";

export type ChatChangeStatus = "applied" | "rejected" | "not_found" | "failed" | "pending";

/** One change record from the server (doc_tools change_records). */
export interface ChatChange {
  id: string;
  section: string;
  op: ChangeSpec["op"];
  quote: string;
  new_text?: string | null;
  before: string;
  after: string;
  after_quote?: string | null;
  undo?: ChangeSpec | null;
  rationale?: string | null;
  status: ChatChangeStatus;
  error?: string | null;
}

/** Parse the ``changes`` field of a chat ``complete`` event. */
export function parseChatChanges(raw: unknown): ChatChange[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is ChatChange =>
      !!c && typeof c === "object" && typeof (c as ChatChange).id === "string" && typeof (c as ChatChange).section === "string",
  );
}

// ── Undo / Redo state ────────────────────────────────────────────────────

export interface ChangeLiveState {
  undone: boolean;
  /** The inverse to apply on the next Undo (Redo can move the anchors). */
  undo: ChangeSpec | null;
}

const _state = new Map<string, ChangeLiveState>();
const _listeners = new Set<() => void>();
let _version = 0;

const _key = (scope: string, id: string) => `${scope}:${id}`;

function _emit() {
  _version += 1;
  for (const l of _listeners) l();
}

function _subscribe(l: () => void) {
  _listeners.add(l);
  return () => _listeners.delete(l);
}

export function changeLiveState(scope: string, change: ChatChange): ChangeLiveState {
  return _state.get(_key(scope, change.id)) ?? { undone: false, undo: change.undo ?? null };
}

/** Re-render when any change's Undo / Redo state moves. */
export function useChangeStateVersion(): number {
  return useSyncExternalStore(_subscribe, () => _version);
}

export interface UndoRedoOutcome {
  done: string[];
  failed: { id: string; error: string }[];
}

/** Undo the applied, not-yet-undone changes — last applied first. */
export async function undoChanges(scope: string, changes: ChatChange[]): Promise<UndoRedoOutcome> {
  const targets = changes.filter((c) => c.status === "applied" && !changeLiveState(scope, c).undone).reverse();
  const specs = targets.map((c) => changeLiveState(scope, c).undo);
  const runnable = targets.filter((_, i) => specs[i] !== null);
  const failed = targets
    .filter((_, i) => specs[i] === null)
    .map((c) => ({ id: c.id, error: "this change can't be undone here" }));
  const results = await enqueueWordWrite(
    () => applyChanges(specs.filter((s): s is ChangeSpec => s !== null)),
    "Undoing the changes",
  );
  const done: string[] = [];
  runnable.forEach((c, i) => {
    const r = results[i];
    if (r?.success) {
      _state.set(_key(scope, c.id), { undone: true, undo: changeLiveState(scope, c).undo });
      done.push(c.id);
    } else failed.push({ id: c.id, error: r?.error ?? "no result" });
  });
  _emit();
  return { done, failed };
}

/** Apply the undone changes again, in their original order. */
export async function redoChanges(scope: string, changes: ChatChange[]): Promise<UndoRedoOutcome> {
  const targets = changes.filter((c) => c.status === "applied" && changeLiveState(scope, c).undone);
  const results = await enqueueWordWrite(
    () =>
      applyChanges(
        targets.map((c) => ({ id: c.id, section: c.section, op: c.op, quote: c.quote, new_text: c.new_text ?? "" })),
      ),
    "Re-applying the changes",
  );
  const done: string[] = [];
  const failed: { id: string; error: string }[] = [];
  targets.forEach((c, i) => {
    const r = results[i];
    if (r?.success) {
      _state.set(_key(scope, c.id), { undone: false, undo: r.undo ?? null });
      done.push(c.id);
    } else failed.push({ id: c.id, error: r?.error ?? "no result" });
  });
  _emit();
  return { done, failed };
}

// ── Reveal in Word ───────────────────────────────────────────────────────

/** Scroll Word to a change and select it: the new text when it stands,
 *  the neighbouring paragraph for a delete, the original text otherwise.
 *  Falls back to the section heading. */
export async function revealChange(scope: string, change: ChatChange): Promise<boolean> {
  const live = changeLiveState(scope, change);
  const standing = change.status === "applied" && !live.undone;
  const candidates: string[] = [];
  if (standing) {
    if (change.after_quote) candidates.push(change.after_quote);
    // A delete leaves nothing to select: its undo anchors on the neighbour.
    if (live.undo?.op === "insert_after" || live.undo?.op === "insert_before") candidates.push(live.undo.quote);
  } else {
    candidates.push(change.quote);
  }
  for (const text of candidates) {
    if (text && (await selectSpanInSection(change.section, text))) return true;
  }
  return navigateToSection(change.section);
}
